import {
  fingerprintFromParts,
  forwardableCaptureTags,
  normalizeFingerprint,
  posthogLaneForCapture,
  posthogLogSeverityForLevel,
  resolveCaptureLevel,
} from '@mondaygpt/constants'

/** One browser capture, scopes already merged (error-reporting.ts). */
export interface ClientCapture {
  /** captureException input: an Error, or any thrown value */
  exception?: unknown
  /** captureMessage input */
  message?: string
  level?: string
  tags: Record<string, unknown>
  extra: Record<string, unknown>
  breadcrumbs: object[]
  fingerprint?: readonly unknown[]
  /** How the capture was made (`generic`, `react.onUncaughtError`, ...) */
  mechanism?: string
}

/** Where a browser capture lands in PostHog. */
export type ClientPosthogRoute =
  | { lane: 'skip'; reason: 'empty' }
  | { lane: 'issue'; error: Error; properties: Record<string, unknown> }
  | {
      lane: 'log'
      level: 'warn' | 'info' | 'debug'
      message: string
      attributes: Record<string, unknown>
    }

function describeNonError(value: unknown): string {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object') {
    const message = (value as { message?: unknown }).message
    if (typeof message === 'string' && message) return message
    const keys = Object.keys(value)
    return `Object captured as exception with keys: ${keys.join(', ') || '(none)'}`
  }
  return String(value)
}

function withoutCollisions(
  base: Record<string, unknown>,
  additions: Record<string, unknown>
): Record<string, unknown> {
  for (const [key, value] of Object.entries(additions)) {
    if (!Object.prototype.hasOwnProperty.call(base, key)) base[key] = value
  }
  return base
}

/** The capture's display text: `Name: message` for errors, the text otherwise. */
export function captureText(capture: Pick<ClientCapture, 'exception' | 'message'>): string {
  const { exception } = capture
  if (exception instanceof Error) return `${exception.name}: ${exception.message}`
  if (exception !== undefined && exception !== null) return `Error: ${describeNonError(exception)}`
  return (capture.message ?? '').trim()
}

/**
 * Route one browser capture to its PostHog lane. The lane rule is the shared
 * `posthogLaneForCapture` in @mondaygpt/constants — the server applies the
 * same one, so a capture lands in the same lane whichever app made it.
 *
 * Globally-unhandled errors never come through here: posthog-js captures
 * those natively (`capture_exceptions`).
 *
 * - Exceptions (an Error, or a thrown non-Error value) become issues unless
 *   captured at `warning`; a non-Error gets a synthetic Error grouped by its
 *   normalized message.
 * - Message-only captures at `error` (console.error(string), explicit
 *   `captureMessage(..., 'error')`) → issue grouped by the normalized message.
 * - Message-only captures at `warning`/`info`/`debug` (the console.warn
 *   bridge — Pusher reconnects, notification permission, SDK chatter — and
 *   explicit warning captures) → PostHog Logs at the matching severity, with
 *   the capture's tags AND extras as attributes.
 * - An explicit `fingerprint` overrides the grouping.
 *
 * `source` is the route marker: `console` when the console bridge originated
 * the capture (it tags `via: console`), `capture` otherwise. A tag that
 * collides with a marker key survives as `tag_<key>`.
 */
export function routeClientCapture(capture: ClientCapture): ClientPosthogRoute {
  const hasException = capture.exception !== undefined && capture.exception !== null
  const error = capture.exception instanceof Error ? capture.exception : undefined
  const message = captureText(capture)
  if (!message) return { lane: 'skip', reason: 'empty' }

  const level = resolveCaptureLevel(capture.level, hasException)
  const source = capture.tags.via === 'console' ? 'console' : 'capture'
  const tags = forwardableCaptureTags(capture.tags)
  const explicitFingerprint = fingerprintFromParts(capture.fingerprint)

  if (posthogLaneForCapture({ level, hasException }) === 'issue') {
    const properties: Record<string, unknown> = {
      ...tags,
      source,
      mechanism: capture.mechanism ?? 'generic',
      $exception_level: level,
    }
    if (explicitFingerprint) {
      properties.$exception_fingerprint = explicitFingerprint
    } else if (!error) {
      // Synthetic Error whose stack points at the facade, so group by message
      properties.$exception_fingerprint = normalizeFingerprint(message)
    }
    withoutCollisions(properties, capture.extra)
    if (capture.breadcrumbs.length > 0) {
      withoutCollisions(properties, { breadcrumbs: capture.breadcrumbs })
    }
    return { lane: 'issue', error: error ?? new Error(message), properties }
  }

  const attributes: Record<string, unknown> = {
    source,
    capture_level: level,
    ...tags,
  }
  withoutCollisions(attributes, capture.extra)
  return { lane: 'log', level: posthogLogSeverityForLevel(level), message, attributes }
}
